import type { AssetTagsResponse, TagOverride } from '@/types';

/** Raw shape tolerant of legacy (pre-Tags-v2) group names during rollout */
type RawAssetTags = {
  asset_id: string;
  tags: Partial<AssetTagsResponse['tags']> & {
    key_points?: TagOverride[];
    ai_speakers?: TagOverride[];
  };
};

// Some API versions return `timestamp_ms_start` instead of `timestamp_ms` on
// the GET response (matching the POST/raw shape). Normalize either field name
// so the UI can rely on `timestamp_ms` consistently.
type RawTag = TagOverride & { timestamp_ms_start?: number | null };
function normalizeTag(t: RawTag): TagOverride {
  return {
    ...t,
    timestamp_ms: t.timestamp_ms ?? t.timestamp_ms_start ?? null,
  };
}

/**
 * Normalizes a GET /assets/{id}/tags response into the fixed Tags v2 shape
 * (people, objects, activities, scenes, topics, locations, descriptions).
 * Folds legacy `key_points`/`ai_speakers` groups (if a transitional response
 * still returns them) into `topics`/`people`, remapping each item's tag_type.
 */
export function normalizeAssetTags(raw: RawAssetTags): AssetTagsResponse {
  const legacyKeyPoints = (raw.tags.key_points ?? []).map((t) => normalizeTag({ ...t, tag_type: 'topic' as const }));
  const legacyAiSpeakers = (raw.tags.ai_speakers ?? []).map((t) => normalizeTag({ ...t, tag_type: 'person' as const }));

  return {
    asset_id: raw.asset_id,
    tags: {
      people: [...(raw.tags.people ?? []).map(normalizeTag), ...legacyAiSpeakers],
      objects: (raw.tags.objects ?? []).map(normalizeTag),
      activities: (raw.tags.activities ?? []).map(normalizeTag),
      scenes: (raw.tags.scenes ?? []).map(normalizeTag),
      topics: [...(raw.tags.topics ?? []).map(normalizeTag), ...legacyKeyPoints],
      locations: (raw.tags.locations ?? []).map(normalizeTag),
      descriptions: (raw.tags.descriptions ?? []).map(normalizeTag),
    },
  };
}
